import { InventoryTagUpdatedEvent } from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_events_pb';
import {
  InventoryTagStatus,
  QuantityCaptureMethod,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_pb';
import { Col, DiffViewer, FieldDisplay, IDField } from '@rematter/ui-web';

import {
  inventoryTagStatusLabels,
  quantityCaptureMethodLabels,
} from '../enumLabels';

type InventoryTagUpdatedDetailProps = {
  event: InventoryTagUpdatedEvent;
};

const getStatusLabel = (status: InventoryTagStatus) =>
  inventoryTagStatusLabels[status] ?? 'Unknown';

const getQuantityCaptureMethodLabel = (method: QuantityCaptureMethod) =>
  quantityCaptureMethodLabels[method] ?? 'Unknown';

export const InventoryTagUpdatedDetail = ({
  event,
}: InventoryTagUpdatedDetailProps) => {
  const previous = event.previousInventoryTag;
  const current = event.updatedInventoryTag;

  if (!previous || !current) {
    return (
      <Col className="@InventoryTagUpdatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const diffs = [
    {
      field: 'tagNumber',
      label: 'Tag Number',
      previous: previous.tagNumber,
      current: current.tagNumber,
    },
    {
      field: 'status',
      label: 'Status',
      previous: getStatusLabel(previous.status),
      current: getStatusLabel(current.status),
    },
    {
      field: 'grossQuantity',
      label: 'Gross Quantity',
      previous: String(previous.grossQuantity),
      current: String(current.grossQuantity),
    },
    {
      field: 'tare',
      label: 'Tare',
      previous: previous.tare != null ? String(previous.tare) : 'N/A',
      current: current.tare != null ? String(current.tare) : 'N/A',
    },
    {
      field: 'itemId',
      label: 'Item ID',
      previous: previous.itemId?.value,
      current: current.itemId?.value,
    },
    {
      field: 'facilityId',
      label: 'Facility ID',
      previous: previous.facilityId?.value,
      current: current.facilityId?.value,
    },
    {
      field: 'isRevised',
      label: 'Is Revised',
      previous: previous.isRevised ? 'Yes' : 'No',
      current: current.isRevised ? 'Yes' : 'No',
    },
    {
      field: 'quantityCaptureMethod',
      label: 'Quantity Capture Method',
      previous: getQuantityCaptureMethodLabel(previous.quantityCaptureMethod),
      current: getQuantityCaptureMethodLabel(current.quantityCaptureMethod),
    },
    {
      field: 'tareCaptureMethod',
      label: 'Tare Capture Method',
      previous: getQuantityCaptureMethodLabel(previous.tareCaptureMethod),
      current: getQuantityCaptureMethodLabel(current.tareCaptureMethod),
    },
  ];

  return (
    <Col className="@InventoryTagUpdatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Inventory Tag
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={current.id?.value} />
          <FieldDisplay label="Tag Number" value={current.tagNumber} />
        </div>
      </Col>

      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
        <span className="mb-2 text-xs font-medium text-neutral/60">
          Changes
        </span>
        <DiffViewer diffs={diffs} previousLabel="Before" currentLabel="After" />
      </Col>
    </Col>
  );
};
